/**
 * AME signature paths and autofill helpers for Technical Log Book sectors.
 */

export const AME_SIGNATURE_STATIC_FIELDS = [
  {
    valuePath: 'ameAuthPersonPilotSignature',
    typePath: 'ameAuthPersonPilotSignatureType',
    label: 'Fuel / oil section'
  },
  {
    valuePath: 'defectReport.ameSignatureAuth',
    typePath: 'defectReport.ameSignatureAuthType',
    label: 'Defect report'
  },
  {
    valuePath: 'ameAuthorizedPersonSignature',
    typePath: 'ameAuthorizedPersonSignatureType',
    label: 'MEL release'
  },
  {
    valuePath: 'bffCertification.ameAuthorizedPersonSignature',
    typePath: 'bffCertification.ameAuthorizedPersonSignatureType',
    label: 'BFF certification'
  }
];

const INSPECTION_AME_VALUE_SUFFIX = 'engineCleaningDetails.ameAuthPersonSignature';
const INSPECTION_AME_TYPE_SUFFIX = 'engineCleaningDetails.ameAuthPersonSignatureType';

export const isAmeSignatureValuePath = (path) => {
  if (!path || typeof path !== 'string') return false;
  if (AME_SIGNATURE_STATIC_FIELDS.some((f) => f.valuePath === path)) return true;
  return path.includes(`.${INSPECTION_AME_VALUE_SUFFIX}`);
};

export const isAmeSignatureTypePath = (path) => {
  if (!path || typeof path !== 'string') return false;
  if (AME_SIGNATURE_STATIC_FIELDS.some((f) => f.typePath === path)) return true;
  return path.includes(`.${INSPECTION_AME_TYPE_SUFFIX}`);
};

export const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split('.');
  let v = obj;
  for (const k of keys) v = v?.[k];
  return v;
};

export const setNestedValue = (obj, path, value) => {
  if (!obj || !path) return;
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (current[key] === undefined || current[key] === null) {
      current[key] = {};
    } else if (typeof current[key] === 'object' && !Array.isArray(current[key])) {
      current[key] = { ...current[key] };
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
};

export const isSignatureEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

export const formatAmeSignaturePresetLabel = (value, signatureType) => {
  if (signatureType === 'NAME' && typeof value === 'string') {
    const name = value.trim();
    return name.length > 40 ? `${name.slice(0, 40)}…` : name;
  }
  if (signatureType === 'DIGITAL') return 'Same digital signature';
  if (signatureType === 'IMAGE') return 'Same image signature';
  return 'Same AME signature';
};

/**
 * Iterate every AME signature slot on a sector (static fields, then inspections).
 * @param {Object} sector
 * @param {(entry: { valuePath: string, typePath: string, label: string, value: *, signatureType: string }) => void} visitor
 */
export const forEachAmeSignatureOnSector = (sector, visitor) => {
  if (!sector || typeof sector !== 'object' || typeof visitor !== 'function') return;

  for (const field of AME_SIGNATURE_STATIC_FIELDS) {
    visitor({
      valuePath: field.valuePath,
      typePath: field.typePath,
      label: field.label,
      value: getNestedValue(sector, field.valuePath),
      signatureType: getNestedValue(sector, field.typePath) || 'NAME'
    });
  }

  const inspections = Array.isArray(sector.inspections) ? sector.inspections : [];
  for (let i = 0; i < inspections.length; i++) {
    const details = inspections[i]?.engineCleaningDetails;
    visitor({
      valuePath: `inspections.${i}.${INSPECTION_AME_VALUE_SUFFIX}`,
      typePath: `inspections.${i}.${INSPECTION_AME_TYPE_SUFFIX}`,
      label: `Inspection ${i + 1} engine cleaning`,
      value: details?.ameAuthPersonSignature,
      signatureType: details?.ameAuthPersonSignatureType || 'NAME'
    });
  }
};

/**
 * First non-empty AME signature on the sector (static fields, then inspections).
 */
export const findPrimaryAmeSignature = (sector) => {
  if (!sector || typeof sector !== 'object') return null;

  let found = null;
  forEachAmeSignatureOnSector(sector, (entry) => {
    if (found || isSignatureEmpty(entry.value)) return;
    found = {
      value: entry.value,
      signatureType: entry.signatureType || 'NAME',
      sourceValuePath: entry.valuePath,
      sourceLabel: entry.label
    };
  });
  return found;
};

/**
 * Unique NAME-type AME strings already present on a sector (excluding one path).
 */
export const collectAmeNamesFromSector = (sector, excludeValuePath = null) => {
  const seen = new Set();
  const names = [];
  forEachAmeSignatureOnSector(sector, (entry) => {
    if (excludeValuePath && entry.valuePath === excludeValuePath) return;
    if ((entry.signatureType || 'NAME') !== 'NAME') return;
    if (typeof entry.value !== 'string') return;
    const name = entry.value.trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    names.push(name);
  });
  return names;
};

/**
 * Unique NAME-type AME strings across all sectors on a TLB entry.
 */
export const collectAmeNamesFromFormData = (formData, excludeValuePath = null) => {
  if (!formData || typeof formData !== 'object') return [];
  const seen = new Set();
  const names = [];
  Object.keys(formData)
    .filter((key) => /^sector\d+$/.test(key))
    .sort()
    .forEach((key) => {
      for (const name of collectAmeNamesFromSector(formData[key], excludeValuePath)) {
        if (seen.has(name)) continue;
        seen.add(name);
        names.push(name);
      }
    });
  return names;
};

/**
 * All non-empty AME signatures on the sector except the field being edited.
 * Used for digital/image reuse dropdowns (and as fallback for NAME).
 */
export const getAmeSignaturePresetsForField = (sector, currentValuePath) => {
  const presets = [];
  const seen = new Set();
  forEachAmeSignatureOnSector(sector, (entry) => {
    if (entry.valuePath === currentValuePath) return;
    if (isSignatureEmpty(entry.value)) return;
    const key = `${entry.signatureType || 'NAME'}::${String(entry.value)}`;
    if (seen.has(key)) return;
    seen.add(key);
    presets.push({
      label: formatAmeSignaturePresetLabel(entry.value, entry.signatureType),
      sectionLabel: entry.label,
      value: entry.value,
      signatureType: entry.signatureType || 'NAME'
    });
  });
  return presets;
};

/**
 * Preset option for SignatureField quick-select (excludes the field being edited).
 * @deprecated Prefer getAmeSignaturePresetsForField / collectAmeNamesFromFormData.
 */
export const getAmeSignaturePresetForField = (sector, currentValuePath) => {
  const presets = getAmeSignaturePresetsForField(sector, currentValuePath);
  return presets[0] || null;
};

/**
 * Fill only empty AME signature fields on a sector copy.
 * Does not keep previously filled fields locked to the source name.
 */
export const applyAmeSignatureAutofillToSector = (
  sector,
  sourceValuePath,
  value,
  signatureType
) => {
  if (!sector || typeof sector !== 'object' || isSignatureEmpty(value)) return sector;

  const type = signatureType || 'NAME';
  const updated = { ...sector };

  for (const field of AME_SIGNATURE_STATIC_FIELDS) {
    if (field.valuePath === sourceValuePath) continue;
    const currentValue = getNestedValue(updated, field.valuePath);
    if (isSignatureEmpty(currentValue)) {
      setNestedValue(updated, field.valuePath, value);
      setNestedValue(updated, field.typePath, type);
    }
  }

  const inspections = Array.isArray(updated.inspections)
    ? updated.inspections.map((insp) => ({
        ...insp,
        engineCleaningDetails: insp?.engineCleaningDetails
          ? { ...insp.engineCleaningDetails }
          : {}
      }))
    : [];

  for (let i = 0; i < inspections.length; i++) {
    const valuePath = `inspections.${i}.${INSPECTION_AME_VALUE_SUFFIX}`;
    if (valuePath === sourceValuePath) continue;
    const details = inspections[i].engineCleaningDetails || {};
    if (isSignatureEmpty(details.ameAuthPersonSignature)) {
      inspections[i] = {
        ...inspections[i],
        engineCleaningDetails: {
          ...details,
          ameAuthPersonSignature: value,
          ameAuthPersonSignatureType: type
        }
      };
    }
  }

  if (inspections.length > 0) {
    updated.inspections = inspections;
  }

  return updated;
};
